import { useEffect, useRef } from 'react';

const BEACON_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes

/**
 * Keeps the server session alive by periodically hitting /beacon.
 * Also checks session immediately when the tab becomes visible again
 * (covers laptop sleep, long tab-away scenarios).
 *
 * @param {boolean} isLoggedIn - Only activate when user is authenticated
 */
function useSessionKeepalive(isLoggedIn) {
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!isLoggedIn) return;

    const pingBeacon = async () => {
      try {
        const response = await fetch('/beacon', { credentials: 'include' });
        if (response.status === 401) {
          console.warn('[SessionKeepalive] Session expired, redirecting to login');
          window.location.href = '/login';
        }
      } catch (error) {
        // Network error — server unreachable. Will retry next interval.
        console.warn('[SessionKeepalive] Beacon failed:', error.message);
      }
    };

    intervalRef.current = setInterval(pingBeacon, BEACON_INTERVAL_MS);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        pingBeacon();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(intervalRef.current);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isLoggedIn]);
}

export default useSessionKeepalive;
